import { useEffect, useState } from 'react';
import {
    Truck, Inbox, Copy, ExternalLink, Package, CheckCircle2, Clock, MapPin,
    Check, ClipboardCheck, Home,
} from 'lucide-react';
import { notify } from '../../components/Notify';
import { getMinhasSolicitacoesApi } from '../../api';
import { CORREIOS_RASTREIO_URL } from '../../config';
import './rastreamento.css';

const SITUACOES = {
    em_transito: { label: 'Em trânsito', cls: 'is-transito', Icon: Truck },
    entregue: { label: 'Entregue', cls: 'is-entregue', Icon: CheckCircle2 },
    preparando: { label: 'Preparando envio', cls: 'is-prep', Icon: Package },
    aguardando_endereco: { label: 'Aguardando endereço', cls: 'is-aguard', Icon: MapPin },
    aguardando_aprovacao: { label: 'Aguardando aprovação', cls: 'is-aguard', Icon: Clock },
};

// Jornada da via física. `NIVEL` diz até que etapa a solicitação chegou — a
// barra de progresso pinta os nós até ali.
const ETAPAS = [
    { key: 'confirmado', label: 'Confirmado', Icon: ClipboardCheck },
    { key: 'preparando', label: 'Preparando', Icon: Package },
    { key: 'caminho', label: 'A caminho', Icon: Truck },
    { key: 'entregue', label: 'Entregue', Icon: Home },
];
const NIVEL = {
    aguardando_aprovacao: 1, aguardando_endereco: 1, preparando: 1,
    em_transito: 2, entregue: 3,
};

const MSG_SEM_CODIGO = {
    aguardando_endereco: 'Complete o endereço de entrega para gerarmos a etiqueta.',
    aguardando_aprovacao: 'Seu pagamento está em análise. Assim que aprovado, preparamos o envio.',
    preparando: 'Estamos preparando a sua via física. O código de rastreio aparece assim que ela for postada.',
};

function formatarDataHora(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('pt-BR') + ' · ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export default function Rastreamento() {
    const [itens, setItens] = useState(null);

    useEffect(() => {
        let vivo = true;
        (async () => {
            try {
                const res = await getMinhasSolicitacoesApi();
                if (vivo) setItens(res?.data?.itens || []);
            } catch {
                if (vivo) setItens([]);
            }
        })();
        return () => { vivo = false; };
    }, []);

    const copiar = (codigo) => {
        navigator.clipboard?.writeText(codigo).then(() => notify('Código copiado!'));
    };

    return (
        <div className="ueb-page">
            <header className="ueb-page-head">
                <span className="ueb-page-ico"><Truck size={22} /></span>
                <div>
                    <h1 className="ueb-page-title">Rastreamento</h1>
                    <p className="ueb-page-sub">Acompanhe a entrega da sua via física.</p>
                </div>
            </header>

            {itens === null ? (
                <div className="pcard" style={{ padding: 40, textAlign: 'center' }}><span className="pspin" style={{ color: 'var(--p-accent)' }} /></div>
            ) : itens.length === 0 ? (
                <div className="ueb-empty pcard">
                    <span className="ueb-empty-ico"><Inbox size={28} /></span>
                    <strong>Nenhuma entrega em andamento</strong>
                    <p>Quando você pedir uma via física, o acompanhamento aparece aqui.</p>
                </div>
            ) : (
                <div className="ueb-rastreio-lista">
                    {itens.map((s) => {
                        const sit = SITUACOES[s.situacao] || { label: s.situacao, cls: '', Icon: Package };
                        const SIcon = sit.Icon;
                        const entregue = s.situacao === 'entregue';
                        const nivel = NIVEL[s.situacao] ?? 1;

                        return (
                            <div key={s.id} className={`pcard ueb-rt-card ${sit.cls}`}>
                                <div className="ueb-rt-head">
                                    <span className={`ueb-rt-badge ${sit.cls}`}><SIcon size={15} /> {sit.label}</span>
                                    <div className="ueb-rt-chips">
                                        {s.modalidade && <span className="ueb-rt-chip">{s.modalidade}</span>}
                                        <span className="ueb-rt-chip ueb-rt-chip--ghost">Ano {s.ano}</span>
                                    </div>
                                </div>

                                {s.cidade && (
                                    <p className="ueb-rt-dest">
                                        <MapPin size={14} /> Entrega em <strong>{s.cidade}{s.estado ? ` - ${s.estado}` : ''}</strong>
                                    </p>
                                )}

                                {/* Barra de progresso da entrega */}
                                <div className="ueb-rt-track">
                                    {ETAPAS.map((et, i) => {
                                        const concluido = i < nivel || (entregue && i <= nivel);
                                        const ativo = i === nivel && !entregue;
                                        const EIcon = et.Icon;
                                        return (
                                            <div key={et.key} className={`ueb-rt-step ${i <= nivel ? 'is-on' : ''} ${ativo ? 'is-active' : ''}`}>
                                                <span className="ueb-rt-node">{concluido ? <Check size={15} /> : <EIcon size={15} />}</span>
                                                <span className="ueb-rt-step-label">{et.label}</span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {s.codigoRastreio ? (
                                    <>
                                        <div className="ueb-rt-codigo">
                                            <div className="ueb-rt-codigo-box">
                                                <span className="ueb-rt-codigo-lbl">Código de rastreio</span>
                                                <code>{s.codigoRastreio}</code>
                                            </div>
                                            <div className="ueb-rt-codigo-acts">
                                                <button className="pbtn pbtn--ghost pbtn--sm" onClick={() => copiar(s.codigoRastreio)}>
                                                    <Copy size={14} /> Copiar
                                                </button>
                                                <a className="pbtn pbtn--primary pbtn--sm" href={CORREIOS_RASTREIO_URL} target="_blank" rel="noreferrer">
                                                    <ExternalLink size={14} /> Ver nos Correios
                                                </a>
                                            </div>
                                        </div>

                                        {Array.isArray(s.eventos) && s.eventos.length > 0 && (
                                            <ol className="ueb-timeline">
                                                {s.eventos.map((ev, i) => (
                                                    <li key={i} className={i === 0 ? 'is-atual' : ''}>
                                                        <span className="ueb-timeline-dot" />
                                                        <div>
                                                            <strong>{ev.descricao}</strong>
                                                            <small className="pfaint">{ev.local} • {formatarDataHora(ev.data)}</small>
                                                        </div>
                                                    </li>
                                                ))}
                                            </ol>
                                        )}
                                    </>
                                ) : (
                                    <div className="ueb-rt-msg">
                                        <span className="ueb-rt-msg-ico"><Clock size={15} /></span>
                                        <span>{MSG_SEM_CODIGO[s.situacao] || 'Assim que a sua via for postada, o código de rastreio aparece aqui.'}</span>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
